import { ChevronLeft, ChevronRight, Grid3x3, Magnet, Scissors, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { detectTempo, onsetEnvelope, toMono } from '../audio/analysis';
import { getSample } from '../audio/registry';
import { DIVISIONS, sliceCount } from '../audio/slice';
import { patternBeats, trackAudible } from '../audio/sequencer';
import { auditionSlice } from '../lib/audition';
import { useBeat } from '../lib/hooks';
import { useStore } from '../store/useStore';
import type { Division } from '../types';
import { Field, NumberField, Select } from './ui/Controls';
import { Knob } from './ui/Knob';
import { Waveform } from './Waveform';

/** Loop editor: tempo, downbeat, chop grid, and a pad bank for auditioning. */
export function ChopView() {
  const project = useStore((s) => s.project);
  const selectedSourceId = useStore((s) => s.selectedSourceId);
  const updateSource = useStore((s) => s.updateSource);
  const patternFromSource = useStore((s) => s.patternFromSource);
  const assignSource = useStore((s) => s.assignSource);
  const assignPattern = useStore((s) => s.assignPattern);
  const selectedTrackId = useStore((s) => s.selectedTrackId);
  const setUi = useStore((s) => s.setUi);
  const notify = useStore((s) => s.notify);
  const set = useStore((s) => s.set);
  const setProjectBpm = (bpm: number) => set((p) => void (p.bpm = bpm));
  const [hoverSlice, setHoverSlice] = useState(-1);
  const beat = useBeat();
  const playing = useStore((s) => s.playing);

  const source =
    project.sources.find((s) => s.id === selectedSourceId) ?? project.sources[0] ?? null;

  if (!source) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="max-w-sm text-center">
          <Scissors size={30} className="mx-auto mb-3 text-ink-600" />
          <h2 className="display text-base">No loop selected</h2>
          <p className="mt-1.5 text-xs leading-relaxed text-ink-400">
            Hit <span className="text-ink-200">Find loops in a track</span> on the left, load a whole
            song, and lift four or eight bars out of it. Happy Chopper locks the grid, chops it and
            drops it on a free track.
          </p>
        </div>
      </div>
    );
  }

  const count = sliceCount(source, project.beatsPerBar);

  /**
   * Where in this loop the sound you're hearing is coming from.
   *
   * A pattern doesn't run through the sample, it jumps around it, so this is
   * resolved through whichever track is playing this source: find its current
   * step, and that step's slice is the region sounding. Reversed steps run
   * their fraction backwards, because that's what you'd hear.
   */
  const playhead = (() => {
    if (!playing) return { slice: -1, fraction: 0 };
    const track =
      project.tracks.find((t) => t.id === selectedTrackId && t.sourceId === source.id) ??
      project.tracks.find((t) => t.sourceId === source.id);
    const pattern = project.patterns.find((p) => p.id === track?.patternId);
    if (!track || !pattern || !trackAudible(project, track)) return { slice: -1, fraction: 0 };

    const total = patternBeats(pattern);
    if (total <= 0) return { slice: -1, fraction: 0 };
    const local = ((beat % total) + total) % total;
    const exact = local * pattern.division;
    const step = pattern.steps[Math.floor(exact) % pattern.length];
    if (!step || step.mute || step.slice < 0) return { slice: -1, fraction: 0 };

    const within = exact - Math.floor(exact);
    return { slice: step.slice, fraction: step.reverse ? 1 - within : within };
  })();
  const nudge = (delta: number) =>
    updateSource(source.id, { offset: Math.max(0, source.offset + delta) });

  const redetect = () => {
    const entry = getSample(source.id);
    if (!entry) return;
    const env = onsetEnvelope(toMono(entry.buffer), entry.buffer.sampleRate);
    const r = detectTempo(env, {
      beatsPerBar: project.beatsPerBar,
      duration: entry.buffer.duration,
    });
    updateSource(source.id, {
      bpm: Math.round(r.bpm * 100) / 100,
      bpmConfidence: r.confidence,
      offset: Math.max(0, r.offset),
    });
    notify(`Re-detected ${r.bpm.toFixed(1)} BPM (${Math.round(r.confidence * 100)}% confidence)`);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto p-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Loop name">
          <input
            className="field w-52"
            value={source.name}
            onChange={(e) => updateSource(source.id, { name: e.target.value })}
          />
        </Field>
        <Field label="Source BPM">
          <div className="flex items-center gap-1">
            <NumberField
              value={source.bpm}
              step={0.1}
              min={20}
              max={400}
              onChange={(v) => updateSource(source.id, { bpm: v })}
            />
            <button
              className="btn btn-icon"
              title="Half tempo"
              onClick={() => updateSource(source.id, { bpm: source.bpm / 2, bars: source.bars / 2 })}
            >
              ÷2
            </button>
            <button
              className="btn btn-icon"
              title="Double tempo"
              onClick={() => updateSource(source.id, { bpm: source.bpm * 2, bars: source.bars * 2 })}
            >
              ×2
            </button>
            <button className="btn btn-icon" title="Re-run tempo detection" onClick={redetect}>
              <Sparkles size={12} />
            </button>
            <button
              className="btn"
              title="Make this loop's tempo the project tempo"
              onClick={() => {
                setProjectBpm(Math.round(source.bpm * 100) / 100);
                notify(`Project tempo → ${source.bpm.toFixed(1)} BPM`);
              }}
            >
              Set project tempo
            </button>
          </div>
        </Field>
        <Field label="Bars">
          <NumberField
            value={source.bars}
            step={0.5}
            min={0.5}
            max={64}
            width="w-16"
            onChange={(v) => updateSource(source.id, { bars: Math.max(0.5, v) })}
          />
        </Field>
        <Field label="Chop grid">
          <Select<string>
            className="w-28"
            value={String(source.division)}
            options={DIVISIONS.map((d) => ({ value: String(d.value), label: d.label }))}
            onChange={(v) => updateSource(source.id, { division: Number(v) as Division })}
          />
        </Field>
        <Field label="Downbeat offset">
          <div className="flex items-center gap-1">
            <button className="btn btn-icon" onClick={() => nudge(-0.005)} title="Nudge back 5 ms">
              <ChevronLeft size={12} />
            </button>
            <NumberField
              value={source.offset * 1000}
              step={1}
              width="w-20"
              suffix="ms"
              onChange={(v) => updateSource(source.id, { offset: Math.max(0, v / 1000) })}
            />
            <button className="btn btn-icon" onClick={() => nudge(0.005)} title="Nudge on 5 ms">
              <ChevronRight size={12} />
            </button>
          </div>
        </Field>
        <button
          className={`btn ${source.snapToTransients ? 'btn-active' : ''}`}
          title="Pull each chop to the nearest detected transient"
          onClick={() => updateSource(source.id, { snapToTransients: !source.snapToTransients })}
        >
          <Magnet size={12} />
          Snap to transients
        </button>
      </div>

      <Waveform
        source={source}
        height={190}
        activeSlice={hoverSlice}
        playSlice={playhead.slice}
        playFraction={playhead.fraction}
        onSliceClick={(i) => auditionSlice(source, i)}
        onScrub={() => undefined}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 rounded-lg border border-ink-700 bg-ink-900 px-3 py-2">
          <Knob
            label="Root"
            value={source.rootPitch}
            min={-24}
            max={24}
            step={1}
            bipolar
            color={source.color}
            format={(v) => `${v > 0 ? '+' : ''}${v.toFixed(0)} st`}
            onChange={(v) => updateSource(source.id, { rootPitch: v })}
          />
          <Knob
            label="Gain"
            value={source.gain}
            min={0}
            max={2}
            color={source.color}
            format={(v) => `${(20 * Math.log10(Math.max(0.001, v))).toFixed(1)} dB`}
            onChange={(v) => updateSource(source.id, { gain: v })}
          />
        </div>

        <div className="mono flex flex-col gap-0.5 text-[10px] text-ink-400">
          <span>
            {count} slices · {DIVISIONS.find((d) => d.value === source.division)?.label}
          </span>
          <span>
            {source.duration.toFixed(2)}s · {source.sampleRate / 1000}kHz ·{' '}
            {source.channels === 2 ? 'stereo' : 'mono'}
          </span>
          <span>
            detector confidence {Math.round(source.bpmConfidence * 100)}% ·{' '}
            {source.transients.length} transients
          </span>
        </div>

        <div className="ml-auto flex gap-2">
          <button
            className="btn"
            onClick={() => {
              const id = patternFromSource(source.id);
              if (id) {
                notify('Pattern created from loop');
                setUi({ view: 'sequence' });
              }
            }}
          >
            <Grid3x3 size={12} />
            New pattern from loop
          </button>
          <button
            className="btn btn-primary"
            disabled={!selectedTrackId && project.tracks.length === 0}
            onClick={() => {
              const trackId = selectedTrackId ?? project.tracks[0]?.id;
              if (!trackId) return;
              assignSource(trackId, source.id);
              const track = useStore.getState().project.tracks.find((t) => t.id === trackId);
              if (!track?.patternId) {
                const pid = patternFromSource(source.id);
                if (pid) assignPattern(trackId, pid);
              }
              notify('Loop assigned to track');
              setUi({ view: 'sequence' });
            }}
          >
            Send to track
          </button>
        </div>
      </div>

      <div>
        <div className="label mb-1.5">Slice pads — click to audition</div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(46px,1fr))] gap-1">
          {Array.from({ length: count }, (_, i) => (
            <button
              key={i}
              onMouseEnter={() => setHoverSlice(i)}
              onMouseLeave={() => setHoverSlice(-1)}
              onClick={() => auditionSlice(source, i)}
              className={`mono rounded-md border py-2 text-[10px] transition-colors active:scale-95 ${
                i === playhead.slice
                  ? 'border-transparent text-ink-100'
                  : 'border-ink-700 bg-ink-850 text-ink-300 hover:border-volt-500 hover:bg-volt-500/10 hover:text-volt-400'
              }`}
              style={
                i === playhead.slice
                  ? { background: source.color, borderColor: source.color }
                  : i % source.division === 0
                    ? { borderColor: `${source.color}66`, color: source.color }
                    : undefined
              }
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
